import { ElementRef, Injectable, OnDestroy } from '@angular/core';
import {
  createNavigationService,
  NavigationConfig,
  NavigationDocument,
  NavigationService as NavService,
} from 'qp-navigation';

@Injectable({
  providedIn: 'root',
})
export class NavigationLibraryService implements OnDestroy {
  config: NavigationConfig = {
    enabledebugLogs: false,
    elementName: 'focusable-element',
    horizontalContainerName: 'horizontal-focusable-container',
    verticalContainerName: 'vertical-focusable-container',
    gridContainerName: 'grid-container',
    gridContainerRowName: 'grid-container-row',
    gridListContainerName: 'gridlist-container',
    backButtonName: 'back-btn',
    backButtonKeys: [81, 69, 461, 10009, 8],
    infinityContainerName: 'infinity-focusable-container',
    focusItselfElementName: 'itself-focusable',
    sectionName: 'container-all',
    containerName: 'focusable-container',
    debounceDelay: 250,
  };
  private navService: NavService;
  private centerActiveElementTimer: any;

  constructor() {
    this.navService = createNavigationService(
      this.config,
      document as NavigationDocument
    );
  }

  ngOnDestroy(): void {
    if (this.centerActiveElementTimer) {
      clearTimeout(this.centerActiveElementTimer);
    }
  }

  init(): void {
    this.navService.init();
  }

  blurActiveElement(): void {
    this.navService.blurActiveElement();
  }

  setIndexContainer(contanierClassName: string, index: number): void {
    this.navService.setIndexContainer(contanierClassName, index);
  }

  getIndexContainer(contanierClassName: string): number {
    return this.navService.getIndexContainer(contanierClassName);
  }

  focus(delay = 10): void {
    this.navService.focus(delay);
  }

  focusPage(containerName: string, delay = 50): void {
    this.navService.focusPage(containerName, delay);
  }

  focusSectionPage(
    sectionName: string,
    containerName = 'focusable-container',
    delay = 50
  ): void {
    const index = this.getIndexContainer(sectionName);
    if (index === 0) {
      this.navService.focusPage(containerName, delay);
    } else {
      this.navService.focusSection(sectionName, delay);
    }
  }

  focusContainer(
    containerClassName: string,
    isHorizontal: boolean,
    customIndex: number,
    resetIndex = true,
    delay = 10
  ): void {
    if (isHorizontal) {
      this.navService.focusHorizontalContainer(
        containerClassName,
        resetIndex,
        customIndex,
        delay
      );
    } else {
      this.navService.focusVerticalContainer(
        containerClassName,
        resetIndex,
        customIndex,
        delay
      );
    }
  }

  focusElement(
    element: HTMLElement,
    horizontalIndex: number,
    verticalIndex: number,
    delay = 0
  ): void {
    this.navService.focusElement(
      element,
      horizontalIndex,
      verticalIndex,
      delay
    );
  }

  isFocusLost(): boolean {
    return document.activeElement === document.getElementsByTagName('body')[0];
  }

  getVerticalIndex(): number {
    return this.navService.getVerticalIndex();
  }

  setVerticalIndex(index: number): void {
    this.navService.setVerticalIndex(index);
  }

  getHorizontalIndex(): number {
    return this.navService.getHorizontalIndex();
  }

  setHorizontalIndex(index: number): void {
    this.navService.setHorizontalIndex(index);
  }

  addAutoscroll(card: Element): void {
    if (
      card &&
      card?.getElementsByTagName('h5')[0]?.scrollWidth >
        card?.getElementsByTagName('h5')[0]?.clientWidth
    ) {
      card.classList.add('autoScroll');
    }
  }

  removeAutoscroll(card: Element): void {
    if (
      card &&
      card?.getElementsByTagName('h5')[0]?.scrollWidth >
        card?.getElementsByTagName('h5')[0]?.clientWidth
    ) {
      card.classList.remove('autoScroll');
    }
  }

  centerActiveElement(scrollContainer: ElementRef): void {
    if (this.centerActiveElementTimer) {
      clearTimeout(this.centerActiveElementTimer);
    }
    this.centerActiveElementTimer = setTimeout(() => {
      if (document.activeElement && document.activeElement.tagName === 'BODY') {
        this.focus();
      }
      const parentActiveElement = document.activeElement?.parentElement;
      let y;
      if (parentActiveElement) {
        y =
          parentActiveElement.offsetTop -
          window.innerHeight / 2 +
          parentActiveElement.getBoundingClientRect().height / 2;
        y = Math.round(y);
      }
      if (scrollContainer) scrollContainer.nativeElement.scrollTop = y;
    }, 200);
  }
}
